import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/helpers/toast';

import { useAuthenticatedMutation } from '@/hooks/auth/use-auth-query-hooks';
import { invalidateRelatedQueries } from '@/lib/query/cache-invalidation';
import { toastApiError } from '@/components/error-handling/toast-api-error';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';

interface UpdateTeamMemberRoleParams {
  accountId: string;
  userId: string;
  role: string;
}

export function useUpdateTeamMemberRole() {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation({
    mutationFn: async ({ accountId, userId, role }: UpdateTeamMemberRoleParams) => {
      const response = await AccountsApiService.updateUserRole(accountId, userId, role);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      toast.success('Team member role updated successfully.');
      // Invalidate both teamMembers and USER_ACCOUNTS for consistency
      invalidateRelatedQueries(queryClient, 'TEAM_MEMBERS', { refetchType: 'active' });
      // Also invalidate with specific accountId for exact match
      void queryClient.invalidateQueries({
        queryKey: ['teamMembers', variables.accountId],
        refetchType: 'active',
      });
    },
    onError: (error: unknown) => {
      toastApiError(error, 'Failed to update team member role.');
    },
  });
}
